import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Box,
	Button,
	CircularProgress,
	Divider,
	FormControlLabel,
	Input,
	Modal,
	Switch,
	Tab,
} from "@mui/material";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import TabContext from "@mui/lab/TabContext";
import { saveExamKeyAdvancedSettings } from "../../../components/Api/ExamGradingEndpoints";

const style = {
	position: "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	width: 1200,
	height: "90dvh",
	bgcolor: "#F5F5F5",
	border: "2px solid #F5F5F5",
	boxShadow: 24,
	p: 2,
};

function toBool(value) {
	return (
		String(value || "")
			.trim()
			.toUpperCase() === "Y"
	);
}

function toVersionSortKey(value) {
	const normalized = String(value || "").trim();
	const parsed = Number.parseInt(normalized, 10);
	const isNumeric = Number.isInteger(parsed) && String(parsed) === normalized;
	if (isNumeric) return [0, parsed];
	return [1, normalized];
}

const DEFAULT_WEIGHT = 1;
const WEIGHT_STEP = 0.25;
const WEIGHT_PRECISION = 3;

function parsePositiveWeight(rawValue) {
	const parsed = Number.parseFloat(String(rawValue ?? "").trim());
	if (!Number.isFinite(parsed) || parsed <= 0) return null;
	return Number(parsed.toFixed(WEIGHT_PRECISION));
}

function roundWeightToNearestQuarter(rawValue) {
	const parsed = parsePositiveWeight(rawValue);
	if (parsed == null) return null;
	const rounded = Math.max(WEIGHT_STEP, Math.round(parsed / WEIGHT_STEP) * WEIGHT_STEP);
	return Number(rounded.toFixed(WEIGHT_PRECISION));
}

function formatWeightValue(rawValue) {
	const parsed = parsePositiveWeight(rawValue);
	if (parsed == null) return "";
	return String(parsed);
}

function formatQuarterWeightValue(rawValue) {
	const rounded = roundWeightToNearestQuarter(rawValue);
	if (rounded == null) return "";
	return String(rounded);
}

function isNonDefaultWeight(rawValue) {
	const parsed = parsePositiveWeight(rawValue);
	return parsed != null && Math.abs(parsed - DEFAULT_WEIGHT) > 1e-9;
}

function getStoredWeight(rawValue) {
	const formatted = formatWeightValue(rawValue);
	return formatted && formatted !== String(DEFAULT_WEIGHT) ? formatted : undefined;
}

function getNormalizedSavedWeight(rawValue) {
	const formatted = formatQuarterWeightValue(rawValue);
	return formatted && formatted !== String(DEFAULT_WEIGHT) ? formatted : undefined;
}

function buildInitialState(examProcessingState) {
	const header = examProcessingState?.header || {};
	const lq = Number.parseInt(header?.lqnum, 10) || 1;
	const hq = Number.parseInt(header?.hqnum, 10) || lq;
	const answerKeys = Array.isArray(examProcessingState?.answer_keys)
		? examProcessingState.answer_keys
		: Object.values(examProcessingState?.answer_keys || {});

	const keys = {};
	let hasAnyMR = false;
	let hasAnyWeight = false;

	answerKeys.forEach((key, index) => {
		const keyId = String(key?.exm_id ?? index + 1);
		const itemsByQuestion = {};

		for (let q = lq; q <= hq; q++) {
			itemsByQuestion[q] = { weight: undefined, omit: false, mr: false };
		}

		for (const item of key?.items || []) {
			const q = Number(item?.item_no);
			if (!Number.isFinite(q)) continue;

			const weight = getStoredWeight(item?.weight);
			const omit = toBool(item?.omit);
			const mr = toBool(item?.mr);
			if (mr) hasAnyMR = true;
			if (isNonDefaultWeight(weight)) hasAnyWeight = true;

			itemsByQuestion[q] = {
				weight,
				omit,
				mr,
			};
		}

		keys[keyId] = {
			exm_id: key?.exm_id,
			version: String(key?.version || "").trim() || String(index + 1),
			itemsByQuestion,
		};
	});

	return {
		state: {
			lqNum: lq,
			hqNum: hq,
			nKeys: answerKeys.length || 0,
			keys,
		},
		advancedSettings: {
			compositeScoring: hasAnyWeight,
			mrscor: hasAnyMR,
		},
	};
}

export function AdvancedSettingsGradingPanel({
	active = true,
	onClose = null,
	token,
	reqnum,
	examProcessingState,
	setSnackbar,
	onSaved,
	embedded = false,
}) {
	const [tabValue, setTabValue] = useState("1");
	const [activeTool, setActiveTool] = useState("");
	const [activeWeightInput, setActiveWeightInput] = useState("");
	const [draft, setDraft] = useState({});
	const [saving, setSaving] = useState(false);
	const [localState, setLocalState] = useState({
		lqNum: 1,
		hqNum: 1,
		nKeys: 0,
		keys: {},
	});
	const [advancedSettings, setAdvancedSettings] = useState({
		compositeScoring: false,
		mrscor: false,
	});

	const isDraggingRef = useRef(false);
	const dragActionRef = useRef("add");
	const dragKeyIndexRef = useRef(null);
	const workingSetRef = useRef(null);
	const rafIdRef = useRef(null);

	const hydrateFromExamState = useCallback(() => {
		const built = buildInitialState(examProcessingState);
		setLocalState(built.state);
		setAdvancedSettings(built.advancedSettings);
		setActiveTool("");
		setActiveWeightInput("");
		setDraft({});
		setTabValue("1");
	}, [examProcessingState]);

	useEffect(() => {
		if (!active) return;
		hydrateFromExamState();
	}, [active, hydrateFromExamState]);

	useEffect(() => {
		const onUp = () => endDrag();
		const onLeave = () => endDrag();

		window.addEventListener("mouseup", onUp);
		window.addEventListener("touchend", onUp, { passive: true });
		window.addEventListener("mouseleave", onLeave);

		return () => {
			window.removeEventListener("mouseup", onUp);
			window.removeEventListener("touchend", onUp);
			window.removeEventListener("mouseleave", onLeave);
		};
	}, []);

	const getVersionKeysArray = useCallback((stateObj) => {
		if (!stateObj?.keys || typeof stateObj.keys !== "object") return [];

		return Object.entries(stateObj.keys)
			.map(([keyId, data]) => ({
				keyId,
				versionLabel: data?.version || keyId,
				data,
			}))
			.sort((a, b) => {
				const [aType, aVal] = toVersionSortKey(a.versionLabel);
				const [bType, bVal] = toVersionSortKey(b.versionLabel);
				if (aType !== bType) return aType - bType;
				if (aType === 0) return aVal - bVal;
				return String(aVal).localeCompare(String(bVal));
			});
	}, []);

	const ensureKey = useCallback(
		(prevState, keyIndex) => {
			const versionsArr = getVersionKeysArray(prevState);
			const entry = versionsArr[keyIndex];
			if (!entry) {
				return { keyId: null, keysCopy: { ...(prevState.keys || {}) }, existing: null };
			}

			const keyId = entry.keyId;
			const keysCopy = { ...(prevState.keys || {}) };
			const existingRaw = keysCopy[keyId] || {};
			const existing = {
				...existingRaw,
				itemsByQuestion: existingRaw.itemsByQuestion || {},
			};
			return { keyId, keysCopy, existing };
		},
		[getVersionKeysArray],
	);

	const getDraftForKey = (keyIndex) => draft[keyIndex] ?? new Set();

	const setDraftForKey = (keyIndex, updater) => {
		setDraft((prev) => {
			const cur = new Set(prev[keyIndex] ?? []);
			const next = updater(cur);
			return { ...prev, [keyIndex]: next };
		});
	};

	const clearDraft = (keyIndex) => setDraftForKey(keyIndex, () => new Set());

	const scheduleDraftRender = () => {
		if (rafIdRef.current) return;
		rafIdRef.current = requestAnimationFrame(() => {
			rafIdRef.current = null;
			const keyIndex = dragKeyIndexRef.current;
			if (keyIndex == null) return;
			const snapshot = new Set(workingSetRef.current || []);
			setDraft((prev) => ({ ...prev, [keyIndex]: snapshot }));
		});
	};

	const beginDrag = (q, keyIndex) => {
		isDraggingRef.current = true;
		dragKeyIndexRef.current = keyIndex;

		const cur = getDraftForKey(keyIndex);
		workingSetRef.current = new Set(cur);
		dragActionRef.current = workingSetRef.current.has(q) ? "remove" : "add";
		if (dragActionRef.current === "add") workingSetRef.current.add(q);
		else workingSetRef.current.delete(q);

		scheduleDraftRender();
	};

	const hoverDrag = (q) => {
		if (!isDraggingRef.current || workingSetRef.current == null) return;
		if (dragActionRef.current === "add") workingSetRef.current.add(q);
		else workingSetRef.current.delete(q);
		scheduleDraftRender();
	};

	const endDrag = () => {
		if (!isDraggingRef.current) return;
		isDraggingRef.current = false;

		const keyIndex = dragKeyIndexRef.current;
		const finalSet = new Set(workingSetRef.current || []);
		setDraft((prev) => ({ ...prev, [keyIndex]: finalSet }));

		dragKeyIndexRef.current = null;
		workingSetRef.current = null;
		if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
		rafIdRef.current = null;
	};

	const columns = 10;
	const numQuestions = localState.hqNum
		? Number(localState.hqNum) - Number(localState.lqNum) + 1
		: 0;
	const numRows = Math.ceil(numQuestions / columns);
	const formattedActiveWeight = formatQuarterWeightValue(activeWeightInput);

	const inRangeQs = useCallback(() => {
		const lq = Number(localState.lqNum);
		const hq = Number(localState.hqNum);
		if (!Number.isFinite(lq) || !Number.isFinite(hq) || hq < lq) return [];
		const arr = [];
		for (let q = lq; q <= hq; q++) arr.push(q);
		return arr;
	}, [localState.hqNum, localState.lqNum]);

	const areAllSelectedInRange = (keyIndex) => {
		const draftSet = getDraftForKey(keyIndex);
		const qs = inRangeQs();
		if (qs.length === 0) return false;
		for (const q of qs) {
			if (!draftSet.has(q)) return false;
		}
		return true;
	};

	const selectAllInRange = (keyIndex) => {
		const allSelected = areAllSelectedInRange(keyIndex);
		if (allSelected) {
			setDraftForKey(keyIndex, () => new Set());
			return;
		}
		setDraftForKey(keyIndex, () => new Set(inRangeQs()));
	};

	const getUnmodifiedQs = (keyIndex) => {
		const { existing } = ensureKey(localState, keyIndex);
		const items = existing?.itemsByQuestion || {};
		const qs = inRangeQs();
		const out = [];
		for (const q of qs) {
			const attrs = items[q] || {};
			const hasWeight = isNonDefaultWeight(attrs.weight);
			const isOmitted = !!attrs.omit;
			const isMR = !!attrs.mr;
			if (!hasWeight && !isOmitted && !isMR) out.push(q);
		}
		return out;
	};

	const selectAllBlankInRange = (keyIndex) => {
		const blankQs = getUnmodifiedQs(keyIndex);
		const current = getDraftForKey(keyIndex);
		let same = current.size === blankQs.length;
		if (same) {
			for (const q of blankQs) {
				if (!current.has(q)) {
					same = false;
					break;
				}
			}
		}
		setDraftForKey(keyIndex, () => (same ? new Set() : new Set(blankQs)));
	};

	const toggleSelectRow = (rowIndex, keyIndex) => {
		const lq = Number(localState.lqNum);
		if (!lq) return;
		const startQ = lq + rowIndex * columns;
		const endQ = Math.min(startQ + columns - 1, Number(localState.hqNum));

		setDraftForKey(keyIndex, (set) => {
			const next = new Set(set);
			for (let q = startQ; q <= endQ; q++) {
				if (next.has(q)) next.delete(q);
				else next.add(q);
			}
			return next;
		});
	};

	const deriveWeightsBuckets = (items) => {
		const buckets = {};
		Object.entries(items || {}).forEach(([qStr, attrs]) => {
			const q = Number(qStr);
			if (!attrs) return;
			const w = formatWeightValue(attrs.weight);
			if (!attrs.omit && w) {
				if (!buckets[w]) buckets[w] = [];
				buckets[w].push(q);
			}
		});
		Object.keys(buckets).forEach((w) => buckets[w].sort((a, b) => a - b));
		return buckets;
	};

	const commitDraftForKey = (keyIndex) => {
		const selected = Array.from(getDraftForKey(keyIndex));
		if (selected.length === 0) return;
		if (activeTool === "weight" && !formattedActiveWeight) return;

		setLocalState((prev) => {
			const { keyId, keysCopy, existing } = ensureKey(prev, keyIndex);
			if (!keyId || !existing) return prev;
			const items = { ...(existing.itemsByQuestion || {}) };

			selected.forEach((q) => {
				items[q] = {
					weight: items[q]?.weight,
					omit: !!items[q]?.omit,
					mr: !!items[q]?.mr,
				};
				if (activeTool === "weight") {
					items[q].weight = formattedActiveWeight === "1" ? undefined : formattedActiveWeight;
				} else if (activeTool === "omit") {
					items[q].omit = !items[q].omit;
				} else if (activeTool === "mr") {
					items[q].mr = !items[q].mr;
				}
			});

			keysCopy[keyId] = {
				...existing,
				itemsByQuestion: items,
				weights: deriveWeightsBuckets(items),
			};
			return { ...prev, keys: keysCopy };
		});

		clearDraft(keyIndex);
	};

	const resetKeyItems = (keyIndex) => {
		setLocalState((prev) => {
			const { keyId, keysCopy, existing } = ensureKey(prev, keyIndex);
			if (!keyId || !existing) return prev;
			const items = { ...(existing.itemsByQuestion || {}) };

			Object.keys(items).forEach((q) => {
				items[q] = { weight: undefined, omit: false, mr: false };
			});

			keysCopy[keyId] = {
				...existing,
				itemsByQuestion: items,
				weights: deriveWeightsBuckets(items),
			};

			return { ...prev, keys: keysCopy };
		});
	};

	const keySummaries = useMemo(() => {
		const versions = getVersionKeysArray(localState);
		return versions.map((entry, idx) => {
			const items = entry.data?.itemsByQuestion || {};
			let total = 0;
			let weighted = 0;
			let mrCount = 0;
			let totalMarks = 0;

			Object.values(items).forEach((attrs) => {
				total += 1;
				const notOmitted = attrs && !attrs.omit;
				const hasWeight = attrs && isNonDefaultWeight(attrs.weight);
				const isMR = attrs && attrs.mr;
				if (hasWeight && notOmitted) weighted += 1;
				if (isMR && notOmitted) mrCount += 1;

				if (notOmitted) {
					const weightValue = parsePositiveWeight(attrs?.weight);
					totalMarks += weightValue ?? DEFAULT_WEIGHT;
				}
			});

			return {
				idx,
				label: entry.versionLabel,
				total,
				weighted,
				mrCount,
				totalMarks,
			};
		});
	}, [localState, getVersionKeysArray]);

	const handleSave = async () => {
		const keysPayload = getVersionKeysArray(localState)
			.map(({ data }) => ({
				exm_id: data?.exm_id,
				items: Object.fromEntries(
					Object.entries(data?.itemsByQuestion || {}).map(([q, attrs]) => [
						q,
						{
							omit: !!attrs?.omit,
							mr: !!attrs?.mr,
							...(getNormalizedSavedWeight(attrs?.weight)
								? { weight: getNormalizedSavedWeight(attrs?.weight) }
								: {}),
						},
					]),
				),
			}))
			.filter((k) => k.exm_id);

		if (keysPayload.length === 0) {
			setSnackbar?.({
				open: true,
				message: "No exam keys available to update.",
				severity: "warning",
			});
			return;
		}

		try {
			setSaving(true);
			await saveExamKeyAdvancedSettings(token, reqnum, keysPayload);
			setSnackbar?.({
				open: true,
				message: "Advanced settings saved.",
				severity: "success",
			});
			if (onSaved) await onSaved();
			if (!embedded && onClose) onClose();
		} catch (error) {
			const apiError = error?.response?.data?.error;
			setSnackbar?.({
				open: true,
				message: apiError || "Failed to save advanced settings.",
				severity: "error",
			});
		} finally {
			setSaving(false);
		}
	};

	const panelContent = (
		<div className={`flex flex-col  ${embedded ? "h-full min-h-0" : "justify-between h-full"}`}>
			<div
				className={`grid min-h-0 gap-4 ${
					embedded ? "grid-cols-1 xl:grid-cols-12 flex-1" : "grid-cols-12 h-[82dvh]"
				}`}
			>
				<div
					className={`border-[1px] bg-white overflow-x-hidden overflow-y-auto shadow-sm ${
						embedded
							? "xl:col-span-3 rounded-md xl:rounded-l-md xl:rounded-r-none"
							: "col-span-3 rounded-l-md"
					}`}
				>
					<div className="flex flex-col h-full p-2 gap-2">
						<div className="font-semibold text-center">
							{embedded ? "Advanced Processing Settings" : "Advanced Settings"}
						</div>
						<Divider />
						<FormControlLabel
							labelPlacement="start"
							control={
								<Switch
									checked={advancedSettings.compositeScoring}
									onChange={(e) =>
										setAdvancedSettings((prev) => ({
											...prev,
											compositeScoring: e.target.checked,
										}))
									}
								/>
							}
							label={<div className="text-sm">Weighted/Composite Scoring</div>}
						/>
						<Divider />
						<FormControlLabel
							labelPlacement="start"
							control={
								<Switch
									checked={advancedSettings.mrscor}
									onChange={(e) =>
										setAdvancedSettings((prev) => ({
											...prev,
											mrscor: e.target.checked,
										}))
									}
								/>
							}
							label={<div className="text-sm">Multiple Response (MR)</div>}
						/>
						<Divider />
						<div className="text-xs text-gray-600">
							Toggle tools on/off while editing. Existing per-item values remain unchanged until you
							apply updates.
						</div>

						<Divider />
						<div className="font-semibold text-sm">Current Coverage</div>
						{keySummaries.length === 0 ? (
							<div className="text-sm text-gray-500">No keys loaded.</div>
						) : (
							<div className="space-y-2">
								{keySummaries.map(({ idx, label, total, weighted, mrCount, totalMarks }) => (
									<div key={idx} className="text-sm text-gray-700">
										<div className="font-medium">Key {label}</div>
										<div>
											{weighted} / {total} weighted
										</div>
										<div>{totalMarks} weighted total</div>
										<div>
											{mrCount} / {total} marked MR
										</div>
									</div>
								))}
							</div>
						)}
					</div>
				</div>
				<div
					className={`min-h-0 overflow-y-auto p-4 bg-white ${
						embedded
							? "xl:col-span-9 rounded-md xl:rounded-r-md xl:rounded-l-none"
							: "col-span-9 rounded-r-md"
					}`}
				>
					<div className="text-primary font-medium pb-2">Configure Answer Key(s)</div>
					<Divider />
					<TabContext value={tabValue}>
						<div className="shadow-sm rounded-t-md border-b bg-white">
							<TabList
								onChange={(_, newValue) => setTabValue(newValue)}
								aria-label="Answer key tabs"
							>
								{Array.from({ length: Number(localState.nKeys) }).map((_, index) => {
									const versions = getVersionKeysArray(localState);
									const label = versions[index]?.versionLabel || String(index + 1);
									return <Tab key={index} label={`Key ${label}`} value={`${index + 1}`} />;
								})}
							</TabList>
						</div>

						{Array.from({ length: Number(localState.nKeys) }).map((_, index) => {
							const { existing } = ensureKey(localState, index);
							const items = existing?.itemsByQuestion || {};

							return (
								<TabPanel value={String(index + 1)} key={index + 1} className="shadow-md bg-white">
									<div className="flex flex-col gap-2">
										<div className="flex flex-wrap items-center gap-2 max-w-[30rem] justify-between">
											<div className="font-medium w-[120px]">Selected Tool:</div>
											<div className="flex flex-wrap gap-2 w-[calc(100%-130px)] justify-between">
												{advancedSettings.compositeScoring && (
													<button
														className={`px-3 py-1 rounded border ${
															activeTool === "weight" ? "bg-primary text-white" : "bg-white"
														}`}
														onClick={() => {
															if (activeTool === "weight") {
																setActiveTool("");
																setActiveWeightInput("");
															} else {
																setActiveTool("weight");
															}
														}}
													>
														Set Weight
													</button>
												)}
												<button
													className={`px-3 py-1 rounded border ${
														activeTool === "omit" ? "bg-primary text-white" : "bg-white"
													}`}
													onClick={() => setActiveTool((prev) => (prev === "omit" ? "" : "omit"))}
												>
													Toggle Omit
												</button>
												{advancedSettings.mrscor && (
													<button
														className={`px-3 py-1 rounded border ${
															activeTool === "mr" ? "bg-primary text-white" : "bg-white"
														}`}
														onClick={() => setActiveTool((prev) => (prev === "mr" ? "" : "mr"))}
													>
														Toggle MR
													</button>
												)}
											</div>
										</div>

										{activeTool === "weight" && advancedSettings.compositeScoring && (
											<div className="flex flex-wrap items-start gap-2 max-w-[30rem] justify-between">
												<div className="font-medium w-[120px]">Weight:</div>
												<div className="flex flex-col gap-1 w-[calc(100%-130px)]">
													<Input
														value={activeWeightInput}
														onChange={(e) => setActiveWeightInput(e.target.value)}
														onBlur={() =>
															setActiveWeightInput(
																(prev) => formatQuarterWeightValue(prev) || prev.trim(),
															)
														}
														placeholder="Enter weight"
														inputProps={{ inputMode: "decimal" }}
														className="w-full max-w-[12rem] rounded border px-2 py-1 bg-white"
													/>
													<div className="text-xs text-gray-600">
														Enter any positive number. Applied values are rounded to the nearest
														0.25.
													</div>
													{formattedActiveWeight && (
														<div className="text-xs text-gray-700">
															Will apply as{" "}
															<span className="font-semibold">{formattedActiveWeight}</span>
														</div>
													)}
												</div>
											</div>
										)}

										<Divider />
										<div className="bg-secondaryOrange/20 px-1 rounded-md flex items-center justify-center">
											{Array.from(getDraftForKey(index)).length} selected
										</div>
										<Divider />

										{localState.hqNum &&
										Number(localState.hqNum) - Number(localState.lqNum) + 1 > 0 ? (
											<>
												<div className="flex gap-2 my-2">
													<button
														onClick={() => selectAllInRange(index)}
														className="px-3 py-2 bg-blue-500 text-white rounded"
													>
														Select All
													</button>
													<button
														onClick={() => selectAllBlankInRange(index)}
														className="px-3 py-2 border-blue-500 border-2 text-blue-500 rounded"
													>
														Select Blank
													</button>
													<button
														disabled={
															activeTool === "" ||
															(activeTool === "weight" && !formattedActiveWeight) ||
															Array.from(getDraftForKey(index)).length === 0
														}
														onClick={() => commitDraftForKey(index)}
														className="ml-auto px-3 py-2 disabled:bg-gray-400 disabled:cursor-not-allowed bg-secondaryGreen text-white rounded"
													>
														Apply
													</button>
												</div>

												{Array.from({ length: numRows }).map((_, rowIndex) => {
													const rowQuestions = [];
													for (let i = 0; i < columns; i++) {
														const qNum = Number(localState.lqNum) + rowIndex * columns + i;
														if (qNum <= Number(localState.hqNum)) rowQuestions.push(qNum);
													}

													return (
														<div key={rowIndex} className="flex items-center gap-2 my-1">
															<button
																className={`${
																	rowQuestions.every((q) => getDraftForKey(index).has(q))
																		? "bg-indigo-500 text-white"
																		: "bg-white"
																} h-4 w-4 flex-shrink-0 rounded border border-gray-300 text-sm`}
																onClick={() => toggleSelectRow(rowIndex, index)}
															></button>
															<div className="grid grid-cols-10 gap-2 flex-grow">
																{rowQuestions.map((q) => {
																	const attrs = items[q] || {};
																	const committedWeight = getStoredWeight(attrs.weight) || null;
																	const isOmitted = !!attrs.omit;
																	const isMR = !!attrs.mr;

																	return (
																		<button
																			key={q}
																			onMouseDown={(e) => {
																				e.preventDefault();
																				beginDrag(q, index);
																			}}
																			onMouseEnter={() => hoverDrag(q)}
																			onTouchStart={() => beginDrag(q, index)}
																			onTouchMove={(e) => {
																				const t = e.touches[0];
																				const el = document.elementFromPoint(t.clientX, t.clientY);
																				const qAttr = el?.getAttribute?.("data-q");
																				if (qAttr) hoverDrag(Number(qAttr));
																			}}
																			data-q={q}
																			className={[
																				"relative p-2 border rounded transition text-sm",
																				getDraftForKey(index).has(q)
																					? "ring-2 ring-indigo-500 bg-white"
																					: "bg-white hover:bg-gray-50",
																				isOmitted ? "opacity-60" : "",
																				"select-none",
																			].join(" ")}
																			title={`Q${q}${committedWeight ? ` | W:${committedWeight}` : ""}${isOmitted ? " | OMIT" : ""}${isMR ? " | MR" : ""}`}
																		>
																			{q}
																			{committedWeight && !isOmitted && (
																				<span className="absolute -top-1 -right-1 text-[10px] leading-none px-1 rounded-full border bg-blue-600 text-white border-blue-700">
																					{committedWeight}
																				</span>
																			)}
																			{isOmitted && (
																				<span className="absolute -bottom-1 -left-1 text-[10px] leading-none px-1 rounded-full border bg-gray-300 text-gray-800 border-gray-400">
																					OMIT
																				</span>
																			)}
																			{isMR && !isOmitted && (
																				<span className="absolute -bottom-1 -right-1 text-[10px] leading-none px-1 rounded-full border bg-amber-400 text-black border-amber-500">
																					MR
																				</span>
																			)}
																		</button>
																	);
																})}
															</div>
														</div>
													);
												})}
												<div className="flex justify-between my-2">
													<button
														onClick={() => clearDraft(index)}
														className="px-3 py-2 bg-gray-300 rounded"
													>
														Clear Selection
													</button>
													<button
														onClick={() => resetKeyItems(index)}
														className="px-3 py-2 text-red-700 hover:underline rounded"
													>
														Reset Applied
													</button>
												</div>
											</>
										) : (
											<p className="text-gray-500 mt-2">No question range loaded.</p>
										)}
									</div>
								</TabPanel>
							);
						})}
					</TabContext>
				</div>
			</div>
			<div className="flex justify-end gap-2 mt-3">
				<Button onClick={hydrateFromExamState} variant="outlined" disabled={saving}>
					Reset Draft
				</Button>
				{onClose && (
					<Button onClick={onClose} variant="outlined" disabled={saving}>
						Cancel
					</Button>
				)}
				<Button onClick={handleSave} variant="contained" disabled={saving}>
					{saving ? (
						<CircularProgress size={20} color="inherit" />
					) : embedded ? (
						"Save Changes"
					) : (
						"Save & Close"
					)}
				</Button>
			</div>
		</div>
	);

	if (embedded) {
		return panelContent;
	}

	return (
		<Modal open={active} onClose={saving ? undefined : onClose}>
			<Box sx={style}>{panelContent}</Box>
		</Modal>
	);
}

export default function AdvancedSettingsGradingModal(props) {
	return <AdvancedSettingsGradingPanel {...props} active={props.open} embedded={false} />;
}
